import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import * as TextStyleExtension from '@tiptap/extension-text-style';
import * as ColorExtension from '@tiptap/extension-color';
import { useRef, useEffect, useMemo, useState } from 'react';
import { Bold, Italic, Underline as UnderlineIcon, List, ListOrdered, Palette, Image as ImageIcon, Loader2, Link as LinkIcon } from 'lucide-react';
import { uploadService } from '@/services/upload';
import { useToast } from '@/hooks/use-toast';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';

interface RichTextEditorProps {
  value: string;
  onChange: (_value: string) => void;
  className?: string;
  placeholder?: string;
}

const BASIC_COLORS = [
  { name: 'Preto', value: '#000000' },
  { name: 'Vermelho', value: '#EF4444' },
  { name: 'Azul', value: '#3B82F6' },
  { name: 'Verde', value: '#10B981' },
  { name: 'Amarelo', value: '#F59E0B' },
  { name: 'Roxo', value: '#8B5CF6' },
  { name: 'Rosa', value: '#EC4899' },
  { name: 'Laranja', value: '#F97316' },
];

export const RichTextEditor = ({
  value,
  onChange,
  className,
  placeholder = 'Digite sua descrição aqui...'
}: RichTextEditorProps) => {
  const isInternalChange = useRef(false);
  const previousValueRef = useRef(value);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [isLinkModalOpen, setIsLinkModalOpen] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();

  const extensions = useMemo(() => [
    StarterKit.configure({
      heading: false,
      underline: false,
    }),
    Underline,
    Image.configure({
      inline: true,
      allowBase64: false,
    }),
    Link.configure({
      openOnClick: false,
      HTMLAttributes: {
        target: '_blank',
        rel: 'noopener noreferrer',
      },
    }),
    TextStyleExtension.TextStyle,
    ColorExtension.Color,
  ], []);

  const editor = useEditor({
    extensions,
    content: value || '',
    onUpdate: ({ editor }) => {
      isInternalChange.current = true;
      const html = editor.getHTML();
      onChange(html);
    },
    editorProps: {
      attributes: {
        class: 'prose prose-sm sm:prose lg:prose-lg xl:prose-2xl mx-auto focus:outline-none min-h-[150px] p-3',
        'data-placeholder': placeholder,
      },
    },
  });

  useEffect(() => {
    if (isInternalChange.current) {
      isInternalChange.current = false;
      previousValueRef.current = value;
      return;
    }

    if (previousValueRef.current === value || !editor) {
      return;
    }

    if (editor.getHTML() !== value) {
      previousValueRef.current = value;
      editor.commands.setContent(value || '');
    }
  }, [value, editor]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (showColorPicker && !target.closest('.color-picker-container')) {
        setShowColorPicker(false);
      }
    };

    if (showColorPicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showColorPicker]);

  const handleImageUpload = async (file: File) => {
    if (!editor) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Arquivo inválido',
        description: 'Por favor, selecione uma imagem.',
        variant: 'error',
      });
      return;
    }

    setIsUploadingImage(true);
    try {
      const { url } = await uploadService.uploadImage(file);
      editor.chain().focus().setImage({ src: url }).run();
      toast({
        title: 'Imagem adicionada',
        description: 'A imagem foi adicionada ao conteúdo.',
      });
    } catch (error) {
      console.error('Erro ao fazer upload da imagem:', error);
      toast({
        title: 'Erro ao fazer upload',
        description: 'Não foi possível fazer upload da imagem. Tente novamente.',
        variant: 'error',
      });
    } finally {
      setIsUploadingImage(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      handleImageUpload(file);
    }
  };

  const handleImageButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleLinkButtonClick = () => {
    const previousUrl = editor.getAttributes('link').href;
    setLinkUrl(previousUrl || '');
    setIsLinkModalOpen(true);
  };

  const handleLinkSubmit = () => {
    if (!linkUrl.trim()) {
      editor.chain().focus().extendMarkRange('link').unsetLink().run();
      setIsLinkModalOpen(false);
      setLinkUrl('');
      return;
    }

    let url = linkUrl.trim();
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      url = `https://${url}`;
    }

    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    setIsLinkModalOpen(false);
    setLinkUrl('');
  };

  const handleLinkRemove = () => {
    editor.chain().focus().extendMarkRange('link').unsetLink().run();
    setIsLinkModalOpen(false);
    setLinkUrl('');
  };

  if (!editor) {
    return null;
  }

  return (
    <div className={`border border-gray-300 rounded-md overflow-visible bg-white ${className || ''}`}>
      <div className="flex items-center gap-1 p-2 border-b border-gray-200 bg-gray-50 relative">
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBold().run()}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
        >
          <Bold className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleItalic().run()}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
        >
          <Italic className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
        >
          <UnderlineIcon className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
        >
          <List className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
        >
          <ListOrdered className="w-4 h-4" />
        </button>
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <button
          type="button"
          onClick={handleLinkButtonClick}
          className={`p-1.5 rounded hover:bg-gray-200 ${editor.isActive('link') ? 'bg-gray-300' : ''}`}
          title="Inserir link"
        >
          <LinkIcon className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={handleImageButtonClick}
          disabled={isUploadingImage}
          className={`p-1.5 rounded hover:bg-gray-200 ${isUploadingImage ? 'opacity-50 cursor-not-allowed' : ''}`}
          title="Inserir imagem"
        >
          {isUploadingImage ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <ImageIcon className="w-4 h-4" />
          )}
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileInputChange}
          className="hidden"
        />
        <div className="w-px h-6 bg-gray-300 mx-1" />
        <div className="relative color-picker-container">
          <button
            type="button"
            onClick={() => setShowColorPicker(!showColorPicker)}
            className={`p-1.5 rounded hover:bg-gray-200 ${showColorPicker ? 'bg-gray-300' : ''}`}
            title="Cor do texto"
          >
            <Palette className="w-4 h-4" />
          </button>
          {showColorPicker && (
            <>
              <div className="fixed inset-0 z-40" onClick={() => setShowColorPicker(false)} />
              <div className="absolute top-full left-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg p-2 z-50 color-picker-container min-w-[120px]">
                <div className="grid grid-cols-4 gap-1">
                  {BASIC_COLORS.map((color) => (
                    <button
                      key={color.value}
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        editor.chain().focus().setColor(color.value).run();
                        setShowColorPicker(false);
                      }}
                      className="w-6 h-6 rounded border border-gray-300 hover:scale-110 transition-transform"
                      style={{ backgroundColor: color.value }}
                      title={color.name}
                    />
                  ))}
                </div>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    editor.chain().focus().unsetColor().run();
                    setShowColorPicker(false);
                  }}
                  className="w-full mt-2 px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded"
                >
                  Remover cor
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <EditorContent editor={editor} />
      <Dialog open={isLinkModalOpen} onOpenChange={setIsLinkModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Inserir Link</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">URL</label>
              <Input
                type="url"
                placeholder="https://exemplo.com"
                value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleLinkSubmit();
                  }
                }}
                autoFocus
              />
            </div>
          </div>
          <DialogFooter>
            {editor.getAttributes('link').href && (
              <Button
                type="button"
                variant="outline"
                onClick={handleLinkRemove}
                className="mr-auto"
              >
                Remover Link
              </Button>
            )}
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setIsLinkModalOpen(false);
                setLinkUrl('');
              }}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              onClick={handleLinkSubmit}
              className="bg-black hover:bg-gray-800 text-white"
            >
              Inserir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <style>{`
        .ProseMirror {
          outline: none;
          min-height: 150px;
          padding: 12px;
        }
        .ProseMirror p.is-editor-empty:first-child::before {
          color: #9ca3af;
          content: attr(data-placeholder);
          float: left;
          height: 0;
          pointer-events: none;
        }
        .ProseMirror ul, .ProseMirror ol {
          padding-left: 1.5rem;
          margin: 0.5rem 0;
        }
        .ProseMirror ul {
          list-style-type: disc;
        }
        .ProseMirror ol {
          list-style-type: decimal;
        }
        .ProseMirror p {
          margin: 0.5rem 0;
        }
        .ProseMirror p:first-child {
          margin-top: 0;
        }
        .ProseMirror p:last-child {
          margin-bottom: 0;
        }
        .ProseMirror h1 {
          font-size: 2em;
          font-weight: bold;
          margin: 0.67em 0;
        }
        .ProseMirror h2 {
          font-size: 1.5em;
          font-weight: bold;
          margin: 0.75em 0;
        }
        .ProseMirror h3 {
          font-size: 1.17em;
          font-weight: bold;
          margin: 0.83em 0;
        }
        .ProseMirror h1:first-child,
        .ProseMirror h2:first-child,
        .ProseMirror h3:first-child {
          margin-top: 0;
        }
        .ProseMirror h1:last-child,
        .ProseMirror h2:last-child,
        .ProseMirror h3:last-child {
          margin-bottom: 0;
        }
        .ProseMirror img {
          max-width: 100%;
          height: auto;
          margin: 0.5rem 0;
          border-radius: 4px;
        }
        .ProseMirror a {
          color: #3b82f6;
          text-decoration: underline;
          cursor: pointer;
        }
        .ProseMirror a:hover {
          color: #2563eb;
        }
      `}</style>
    </div>
  );
};
